/**
 * skailify — pure, individually-testable helpers for importing an existing
 * coding-agent skill (e.g. a Claude Code skill from `~/.claude/skills/`) into a
 * skaile library. Composed by the `asset import` command and the `skailify`
 * factory skill. See each export's TSDoc for specifics.
 *
 * @docLink cli/commands/asset#import
 */

import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import {
  isValidAssetName,
  loadSkPublishManifest,
  parseFrontmatter,
  saveSkPublishManifest,
  slugifyAssetName,
} from "@skaile/workspaces/core";
import type { AssetEntry, SkPublishManifest } from "@skaile/workspaces/core";
import { stringify as stringifyYaml } from "yaml";

/** Default authoring version stamped on a skill that ships none. */
export const DEFAULT_IMPORT_VERSION = "0.1.0";
/** Default lifecycle stage stamped on an imported skill. */
export const DEFAULT_IMPORT_STAGE = "alpha";

/**
 * Where coding-agent skills live on disk, highest precedence first. The
 * `SKAILE_CLAUDE_SKILLS_DIR` override exists so tests (and non-default setups)
 * can point the resolver at a fixture tree without touching `$HOME`.
 */
export function claudeSkillRoots(): string[] {
  const roots: string[] = [];
  const override = process.env.SKAILE_CLAUDE_SKILLS_DIR;
  if (override) roots.push(override);
  roots.push(path.join(os.homedir(), ".claude", "skills"));
  return roots;
}

export interface ResolvedImportSource {
  /** Absolute path to the skill directory. */
  dir: string;
  /** Absolute path to the skill's SKILL.md. */
  skillFile: string;
  /** How the source was located — for human-facing messages. */
  via: "path" | "claude-skills";
}

/**
 * Resolve an `import` argument to a concrete skill directory. A value that
 * exists on disk (file or dir) is used verbatim; otherwise it is treated as a
 * bare skill name and looked up under {@link claudeSkillRoots}.
 *
 * Returns `null` when nothing matches so the caller can print a tailored error.
 */
export function resolveImportSource(nameOrPath: string): ResolvedImportSource | null {
  // Explicit path (relative or absolute) wins.
  const asPath = path.resolve(nameOrPath);
  if (fs.existsSync(asPath)) {
    const dir = fs.statSync(asPath).isDirectory() ? asPath : path.dirname(asPath);
    const skillFile = path.join(dir, "SKILL.md");
    if (fs.existsSync(skillFile)) return { dir, skillFile, via: "path" };
  }
  // Bare name → coding-agent skill roots.
  if (!nameOrPath.includes(path.sep) && !nameOrPath.startsWith(".")) {
    for (const root of claudeSkillRoots()) {
      const dir = path.join(root, nameOrPath);
      const skillFile = path.join(dir, "SKILL.md");
      if (fs.existsSync(skillFile)) return { dir, skillFile, via: "claude-skills" };
    }
  }
  return null;
}

export interface NormalizeOptions {
  /** Fallback name when frontmatter has none (usually the source dir name). */
  fallbackName?: string;
  /** Version to stamp when the skill ships none. */
  version?: string;
  /** Stage to stamp when the skill ships none. */
  stage?: string;
}

export interface NormalizeResult {
  /** Rewritten SKILL.md content (frontmatter + body). */
  content: string;
  /** Canonical asset name (slugified if the source was non-compliant). */
  name: string;
  /** The name as it appeared in the source, when it differed from `name`. */
  originalName?: string;
  /** True when `content` differs from the input (metadata added, name slugified, …). */
  changed: boolean;
  /** Human-facing notes about what was normalized. */
  notes: string[];
}

/**
 * Bring a coding-agent `SKILL.md` into the skaile schema without touching the
 * body: ensure a canonical lowercase-kebab `name`, and a `metadata:` block
 * carrying at least `version`, `stage`, and `tags`. Idempotent — a SKILL.md
 * that already conforms comes back unchanged (`changed: false`).
 *
 * A Claude Code skill only requires `name` + `description`; skaile additionally
 * expects the `metadata:` block, so this is the transform `asset migrate`'s raw
 * byte-copy skips.
 */
export function normalizeSkillContent(raw: string, opts: NormalizeOptions = {}): NormalizeResult {
  const notes: string[] = [];
  const { data, body } = parseFrontmatter(raw);

  // ── name ─────────────────────────────────────────────────────────────────
  const rawName = typeof data.name === "string" && data.name.trim() ? data.name.trim() : undefined;
  const sourceName = rawName ?? opts.fallbackName ?? "";
  let name = sourceName;
  let originalName: string | undefined;
  if (!sourceName) {
    throw new Error("skill has no `name` in frontmatter and no fallback was provided");
  }
  if (!isValidAssetName(sourceName)) {
    name = slugifyAssetName(sourceName);
    originalName = sourceName;
    notes.push(`renamed "${sourceName}" → "${name}" (canonical kebab-case)`);
  }

  // ── metadata block ─────────────────────────────────────────────────────────
  const existingMeta =
    data.metadata && typeof data.metadata === "object" && !Array.isArray(data.metadata)
      ? { ...(data.metadata as Record<string, unknown>) }
      : {};

  if (existingMeta.version == null) {
    existingMeta.version = opts.version ?? DEFAULT_IMPORT_VERSION;
    notes.push(`stamped metadata.version: ${existingMeta.version}`);
  }
  if (existingMeta.stage == null) {
    existingMeta.stage = opts.stage ?? DEFAULT_IMPORT_STAGE;
    notes.push(`stamped metadata.stage: ${existingMeta.stage}`);
  }
  if (existingMeta.tags == null) {
    // Promote top-level `keywords` (the agentskills.io convention) into tags.
    const keywords = Array.isArray(data.keywords) ? (data.keywords as unknown[]) : [];
    existingMeta.tags = keywords.filter((k): k is string => typeof k === "string");
    if ((existingMeta.tags as string[]).length > 0) {
      notes.push("promoted top-level keywords → metadata.tags");
    }
  }

  // ── re-serialize, key order: name, description, metadata, then the rest ────
  const ordered: Record<string, unknown> = { name };
  if (data.description != null) ordered.description = data.description;
  ordered.metadata = existingMeta;
  for (const [k, v] of Object.entries(data)) {
    if (k === "name" || k === "description" || k === "metadata" || k === "keywords") continue;
    ordered[k] = v;
  }

  const fm = stringifyYaml(ordered).trimEnd();
  const content = `---\n${fm}\n---\n\n${body}\n`;
  // `changed` tracks semantic mutation, not formatting — every real change adds
  // a note, so a conformant skill stays `false` despite YAML re-quoting.
  const changed = notes.length > 0;
  return { content, name, originalName, changed, notes };
}

export interface EnsureDomainResult {
  /** Absolute path to the domain directory. */
  dir: string;
  /** Publisher slug derived for this domain (the dir name, kebab-cased). */
  publisher: string;
  /** True when this call created the directory / DOMAIN.md. */
  created: boolean;
}

const DOMAIN_TEMPLATE = (name: string, publisher: string, description: string) => {
  // stringifyYaml quotes/escapes values that would otherwise break the scalar.
  const fm = stringifyYaml({ name, publisher, description }).trimEnd();
  return `---\n${fm}\n---\n\n# ${name}\n\n${description}\n`;
};

/**
 * Scaffold a domain directory under a library: `<lib>/<domain>/DOMAIN.md`. A
 * domain is the first-level dir of an asset repo and defines the publisher
 * namespace, so assets placed beneath it publish as `@<publisher>/<name>`.
 * No-op (returns `created: false`) when the DOMAIN.md already exists.
 */
export function ensureDomain(
  libraryPath: string,
  domain: string,
  opts: { description?: string; publisher?: string } = {},
): EnsureDomainResult {
  const dir = path.join(libraryPath, domain);
  const domainFile = path.join(dir, "DOMAIN.md");
  const publisher = opts.publisher ?? slugifyAssetName(domain);
  if (fs.existsSync(domainFile)) {
    return { dir, publisher, created: false };
  }
  fs.mkdirSync(dir, { recursive: true });
  const description = opts.description ?? `The ${domain} domain.`;
  fs.writeFileSync(domainFile, DOMAIN_TEMPLATE(domain, publisher, description), "utf8");
  return { dir, publisher, created: true };
}

/** List the domains in a library — first-level dirs that carry a DOMAIN.md. */
export function listDomains(libraryPath: string): string[] {
  if (!fs.existsSync(libraryPath)) return [];
  return fs
    .readdirSync(libraryPath, { withFileTypes: true })
    .filter((e) => e.isDirectory() && fs.existsSync(path.join(libraryPath, e.name, "DOMAIN.md")))
    .map((e) => e.name)
    .sort();
}

export interface UpsertAssetInput {
  kind: string;
  name: string;
  /** Repo-relative path to the asset directory (the AssetEntry `root`). */
  root: string;
  version?: string;
  publisher?: string;
  description?: string;
  keywords?: string[];
}

export interface UpsertAssetResult {
  manifestPath: string;
  /** True when a new entry was appended, false when an existing one was updated. */
  added: boolean;
}

/**
 * Auto-wire an asset into a library's `skaile.manifest.yaml` (creating the
 * manifest when absent). Matches an existing entry by `(kind, name)` and
 * updates it in place; otherwise appends. This is the manifest half of import
 * that a plain byte-copy (`asset migrate`) leaves stale.
 */
export function upsertManifestAsset(
  libraryPath: string,
  input: UpsertAssetInput,
): UpsertAssetResult {
  const manifest: SkPublishManifest = loadSkPublishManifest(libraryPath) ?? { assets: [] };
  const assets: AssetEntry[] = Array.isArray(manifest.assets) ? [...manifest.assets] : [];

  const entry: AssetEntry = { kind: input.kind, name: input.name, root: input.root };
  if (input.version) entry.version = input.version;
  if (input.publisher) entry.publisher = input.publisher;
  if (input.description) entry.description = input.description;
  if (input.keywords && input.keywords.length > 0) entry.keywords = input.keywords;

  const idx = assets.findIndex((a) => a.kind === input.kind && a.name === input.name);
  const added = idx < 0;
  if (added) {
    assets.push(entry);
  } else {
    assets[idx] = { ...assets[idx], ...entry };
  }

  const manifestPath = saveSkPublishManifest(libraryPath, { ...manifest, assets });
  return { manifestPath, added };
}
